import { IAPIRoutes } from "../../../../config";

import { ITimeProvider } from "../providers/provider";
import {
  formatDaily3DData,
  formatDailyData,
  formatHourly3DData,
  formatMotion,
  processCDLData,
} from "../workers/cdlWorker";
import CDLWS from "./cdl_ws.js";

const env = import.meta.env;

let instance = await CDLWS();

/* Input: base64-encoded string containing CDL timestamps
 * Output: array of Date elements
 */
export const decode_cdl_timestamps = async (b64Ts: string) => {
  const fetchUrl = "data:application/octet-binary;base64," + b64Ts;
  const rawFetch = await fetch(fetchUrl);
  const rawTs = await rawFetch.arrayBuffer();
  const rawTsU8 = new Uint8Array(rawTs);

  var ptrIn = instance._malloc(rawTsU8.length);
  instance.HEAP8.set(rawTsU8, ptrIn);
  const ptrOut = instance._cdl_decode_ts(ptrIn, rawTsU8.length);
  if (!ptrOut) {
    throw new Error("TS conversion failed.");
  }

  const nTsOut = instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT];

  var outArr = [];
  for (var i = 0; i < nTsOut; i++) {
    outArr.push(
      new Date(
        instance.HEAPF64[ptrOut / Float64Array.BYTES_PER_ELEMENT + i + 1]
      )
    );
  }

  instance._free(ptrIn);
  instance._free(ptrOut);

  return outArr;
};

const rrCDLFields = "rr_3s.ds_hrly,rr_3s.ds_dly";
const mvmtFields = `mvmt_in_range.ds_min.thr(>10000).ds_hour_sum.agg_hourly_mean`;
const bedExitFields = `bed_exit_prob.ma(5).thr(>0.65).cooldown(40).ds_hour_sum.agg_hourly_mean`;
const heartRateFields = `hr_3s.ds_hrly,hr_3s.ds_dly`;
const illuminationFields = `illum_r_mean.ds_hrly,illum_g_mean.ds_hrly,illum_b_mean.ds_hrly,illum_w_mean.ds_hrly,illum_r_max.ds_hrly,illum_g_max.ds_hrly,illum_b_max.ds_hrly,illum_w_max.ds_hrly`;

export const CDLRepositoryFactory = ({
  APIProvider,
  APIProvider2,
  AuthService,
  APIRoutes,
  timeDecompressionService,
  timeProvider,
}: {
  APIProvider: IAPIProvider;
  APIProvider2: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
  timeDecompressionService: ITimeDecompressionService;
  timeProvider: ITimeProvider;
}) => {
  const getPatientDataFromCDL: ICDLRepository["getPatientDataFromCDL"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    let { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: rrCDLFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // Check response type

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);
      const processedHourlyData = formatHourly3DData(
        processedData,
        "rr_3s.ds_hrly",
        "rr_3s",
        timezone
      );

      const processedDailyData = formatDaily3DData(
        processedData,
        "rr_3s.ds_dly",
        "rr_3s",
        timezone
      );
      const categorizedData: AwaitableReturn<getPatientDataFromCDL>["data"] = {
        "1D": [],
        "3D": [],
        "5D": [],
        "7D": [],
        "30D": [],
        "6M": [],
        "1Y": [],
        dailyExtra: [],
        hourlyExtra: [],
      };

      const thirtyDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(31, "days");
      const thirtySevenDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(37, "days");
      const sixMonthsAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(180, "days");
      const oneYearAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(365, "days");
      processedDailyData.forEach((item) => {
        // Convert the item's timestamp into a date/time object using the appropriate library method
        const date = timeProvider.dateLibrary(item.dt);

        // Check if the item's date is after the threshold
        if (date.isAfter(thirtyDaysAgo)) {
          categorizedData["30D"].push(item);
        }
        if (date.isAfter(sixMonthsAgo)) {
          categorizedData["6M"].push(item);
        }
        if (date.isAfter(oneYearAgo)) {
          categorizedData["1Y"].push(item);
        }
        if (date.isAfter(thirtySevenDaysAgo)) {
          categorizedData["dailyExtra"].push(item);
        }
      });
      processedHourlyData.forEach((item) => {
        const date = timeProvider.dateLibrary.tz(item.dt, timezone);
        const now = timeProvider.dateLibrary(params.date_to);

        // Last 24 hours
        if (date.isAfter(now.clone().subtract(25, "hours"))) {
          categorizedData["1D"].push(item);
        }
        // Last 72 hours
        if (date.isAfter(now.clone().subtract(73, "hours"))) {
          categorizedData["3D"].push(item);
        }
        // Last 120 hours (5 days)
        if (date.isAfter(now.clone().subtract(121, "hours"))) {
          categorizedData["5D"].push(item);
        }
        // Last 168 hours (7 days)
        if (date.isAfter(now.clone().subtract(169, "hours"))) {
          categorizedData["7D"].push(item);
        }
        if (date.isAfter(now.clone().subtract(145, "hours"))) {
          categorizedData["hourlyExtra"].push(item);
        }
      });

      // 🔍 LOG: RR Data counts
      // console.log(
      //   "📊 [RR Data] Total hourly points from API:",
      //   processedHourlyData.length
      // );
      // console.log("📊 [RR Data] Categorized counts:", {
      //   "1D": categorizedData["1D"].length,
      //   "3D": categorizedData["3D"].length,
      //   "5D": categorizedData["5D"].length,
      //   "7D": categorizedData["7D"].length,
      //   "30D": categorizedData["30D"].length,
      // });

      return {
        success: true,
        data: categorizedData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getPatientDailyRRData: ICDLRepository["getPatientDailyRRData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    let { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const response = (await GET(
      APIRoutes.apiPatientGraphData.replace(
        "{patient_id}",
        patientId?.toString()
      ),
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: "rr_3s.ds_dly",
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as CDL_API_RESPONSE_INTERNAL;
    if (response.status !== 404) {
      // Check response type

      const processedData = await processCDLData(response);
      const processedDailyData = formatDailyData(
        processedData,
        "rr_3s.ds_dly",
        "rr_3s",
        response.timezone
      );
      return {
        success: true,
        data: processedDailyData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getPatientDailyRRWithoutProcessing: ICDLRepository["getPatientDailyRRWithoutProcessing"] =
    async (payload) => {
      const { patient_id: patientId } = payload;
      let { ...params } = payload;
      const { GET } = APIProvider;
      const { getAuthenticationAccessToken } = AuthService;
      const response = (await GET(
        APIRoutes.apiPatientGraphData.replace(
          "{patient_id}",
          patientId?.toString()
        ),
        {
          date_from: params.date_from,
          date_to: params.date_to,
          db: "3s",
          fields: "rr_3s.ds_dly",
        },
        {
          Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
        },
        payload.signal
      )) as CDL_API_RESPONSE_INTERNAL;
      if (response.status !== 404) {
        // Check response type

        return {
          success: true,
          data: response,
        };
      }

      return {
        success: false,
        error: { message: "Something went wrong" },
      };
    };

  const getPatientHourlyRRWithoutProcessingV2: ICDLRepository["getPatientHourlyRRWithoutProcessingV2"] =
    async (payload) => {
      const { patient_ids: patientIds, signal, date_from, date_to } = payload;
      const { getAuthenticationAccessToken } = AuthService;

      try {
        const token = await getAuthenticationAccessToken();

        // Construct the query parameters
        const queryParams = new URLSearchParams({
          date_from,
          date_to,
          db: "3s",
          fields: "rr_3s.ds_hrly",
        });

        patientIds.forEach((id) => {
          queryParams.append("patient_ids", id.toString());
        });

        const response = await fetch(
          `${env.VITE_API_GATEWAY_URL_CDL_DEV}${
            APIRoutes.apiPatientGraphDataV2
          }?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal, // Attach the abort signal
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data: { [patientId: string]: CDL_API_RESPONSE_INTERNAL } =
          await response.json();

        return {
          success: true,
          data,
        };
      } catch (error) {
        // @ts-ignore
        if (error && error?.name === "AbortError") {
          // console.log("Fetch request aborted");
          return {
            success: false,
            error: { message: "Request was aborted" },
          };
        }

        console.error("Fetch error:", error);
        return {
          success: false,
          error: { message: "Something went wrong" },
        };
      }
    };

  const getPatientHourlyHRWithoutProcessingV2: ICDLRepository["getPatientHourlyHRWithoutProcessingV2"] =
    async (payload) => {
      const { patient_ids: patientIds, signal, date_from, date_to } = payload;
      const { getAuthenticationAccessToken } = AuthService;

      try {
        const token = await getAuthenticationAccessToken();

        // Construct the query parameters
        const queryParams = new URLSearchParams({
          date_from,
          date_to,
          db: "HR3s",
          fields: "hr_3s.ds_hrly",
        });

        patientIds.forEach((id) => {
          queryParams.append("patient_ids", id.toString());
        });

        const response = await fetch(
          `${env.VITE_API_GATEWAY_URL_CDL_DEV}${
            APIRoutes.apiPatientGraphDataV2
          }?${queryParams.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal, // Attach the abort signal
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data: { [patientId: string]: CDL_API_RESPONSE_INTERNAL } =
          await response.json();

        return {
          success: true,
          data,
        };
      } catch (error) {
        // @ts-ignore
        if (error && error?.name === "AbortError") {
          console.log("Fetch request aborted");
          return {
            success: false,
            error: { message: "Request was aborted" },
          };
        }

        console.error("Fetch error:", error);
        return {
          success: false,
          error: { message: "Something went wrong" },
        };
      }
    };

  const getBedExitMotionData: ICDLRepository["getBedExitMotionData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    let { ...params } = payload;
    const { GET } = APIProvider;
    const { getAuthenticationAccessToken } = AuthService;

    const response = (await GET(
      APIRoutes.apiPatientGraphData.replace(
        "{patient_id}",
        patientId?.toString()
      ),
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: `${mvmtFields},${bedExitFields}`,
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as CDL_API_RESPONSE_INTERNAL;

    if (response.message !== "No Circadia data found for the patient") {
      const processedData = await processCDLData(response);
      const processedMotionData = formatMotion(
        processedData,
        "mvmt_in_range.ds_min.thr(>10000).ds_hour_sum.agg_hourly_mean",
        "mvmt_in_range"
      );

      const processedBedExitData = formatMotion(
        processedData,
        "bed_exit_prob.ma(5).thr(>0.65).cooldown(40).ds_hour_sum.agg_hourly_mean",
        "bed_exit_prob"
      );

      return {
        success: true,
        data: {
          bedExit: processedBedExitData,
          motion: processedMotionData,
        },
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getHeartRateData: ICDLRepository["getHeartRateData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    let { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "HR3s",
        fields: heartRateFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // Check response type

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);

      const processedHourlyData = formatHourly3DData(
        processedData,
        "hr_3s.ds_hrly",
        "hr_3s",
        timezone
      );

      const processedDailyData = formatDaily3DData(
        processedData,
        "hr_3s.ds_dly",
        "hr_3s",
        timezone
      );

      const categorizedData: AwaitableReturn<getPatientDataFromCDL>["data"] = {
        "1D": [],
        "3D": [],
        "5D": [],
        "7D": [],
        "30D": [],
        "6M": [],
        "1Y": [],
        dailyExtra: [],
        hourlyExtra: [],
      };

      const thirtyDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(31, "days");
      const thirtySevenDaysAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(37, "days");
      const sixMonthsAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(180, "days");
      const oneYearAgo = timeProvider
        .dateLibrary(params.date_to)
        .subtract(365, "days");
      processedDailyData.forEach((item) => {
        // Convert the item's timestamp into a date/time object using the appropriate library method
        const date = timeProvider.dateLibrary(item.dt);

        // Check if the item's date is after the threshold
        if (date.isAfter(thirtyDaysAgo)) {
          categorizedData["30D"].push(item);
        }
        if (date.isAfter(sixMonthsAgo)) {
          categorizedData["6M"].push(item);
        }
        if (date.isAfter(oneYearAgo)) {
          categorizedData["1Y"].push(item);
        }
        if (date.isAfter(thirtySevenDaysAgo)) {
          categorizedData["dailyExtra"].push(item);
        }
      });
      processedHourlyData.forEach((item) => {
        const date = timeProvider.dateLibrary.tz(item.dt, timezone);
        const now = timeProvider.dateLibrary(params.date_to);

        // Last 24 hours
        if (date.isAfter(now.clone().subtract(25, "hours"))) {
          categorizedData["1D"].push(item);
        }
        // Last 72 hours
        if (date.isAfter(now.clone().subtract(73, "hours"))) {
          categorizedData["3D"].push(item);
        }
        // Last 120 hours (5 days)
        if (date.isAfter(now.clone().subtract(121, "hours"))) {
          categorizedData["5D"].push(item);
        }
        // Last 168 hours (7 days)
        if (date.isAfter(now.clone().subtract(169, "hours"))) {
          categorizedData["7D"].push(item);
        }
        if (date.isAfter(now.clone().subtract(145, "hours"))) {
          categorizedData["hourlyExtra"].push(item);
        }
      });

      // 🔍 LOG: HR Data counts
      // console.log(
      //   "💓 [HR Data] Total hourly points from API:",
      //   processedHourlyData.length
      // );
      // console.log("💓 [HR Data] Categorized counts:", {
      //   "1D": categorizedData["1D"].length,
      //   "3D": categorizedData["3D"].length,
      //   "5D": categorizedData["5D"].length,
      //   "7D": categorizedData["7D"].length,
      //   "30D": categorizedData["30D"].length,
      // });

      return {
        success: true,
        data: categorizedData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  const getIlluminationData: ICDLRepository["getIlluminationData"] = async (
    payload
  ) => {
    const { patient_id: patientId } = payload;
    let { ...params } = payload;
    const { GET } = APIProvider2;
    const { getAuthenticationAccessToken } = AuthService;

    // console.log("💡 [Illumination] Fetching data for patient:", patientId);
    // console.log(
    //   "💡 [Illumination] Date range:",
    //   params.date_from,
    //   "to",
    //   params.date_to
    // );
    // console.log("💡 [Illumination] Fields:", illuminationFields);

    const apiResponse = (await GET(
      APIRoutes.apiPatientGraphDataV2,
      {
        date_from: params.date_from,
        date_to: params.date_to,
        db: "3s",
        fields: illuminationFields,
        patient_ids: [patientId],
      },
      {
        Authorization: `Bearer ${await getAuthenticationAccessToken()}`,
      }
    )) as { [patientId: string]: CDL_API_RESPONSE_INTERNAL };

    if (apiResponse) {
      // console.log(
      //   "💡 [Illumination] Raw API response keys:",
      //   Object.keys(apiResponse)
      // );
      // console.log(
      //   "💡 [Illumination] Patient data keys:",
      //   Object.keys(apiResponse[patientId] || {})
      // );

      const { timezone } = apiResponse[patientId];
      const processedData = await processCDLData(apiResponse[patientId]);

      // console.log(
      //   "💡 [Illumination] Processed data keys:",
      //   Object.keys(processedData)
      // );
      // console.log(
      //   "💡 [Illumination] Sample processed data:",
      //   JSON.stringify(processedData, null, 2).substring(0, 500)
      // );

      // Process each illumination field
      const illuminationData: any = {
        r_mean: [],
        g_mean: [],
        b_mean: [],
        w_mean: [],
        r_max: [],
        g_max: [],
        b_max: [],
        w_max: [],
      };

      // Process each field
      const fields = [
        { key: "illum_r_mean.ds_hrly", target: "r_mean", base: "illum_r_mean" },
        { key: "illum_g_mean.ds_hrly", target: "g_mean", base: "illum_g_mean" },
        { key: "illum_b_mean.ds_hrly", target: "b_mean", base: "illum_b_mean" },
        { key: "illum_w_mean.ds_hrly", target: "w_mean", base: "illum_w_mean" },
        { key: "illum_r_max.ds_hrly", target: "r_max", base: "illum_r_max" },
        { key: "illum_g_max.ds_hrly", target: "g_max", base: "illum_g_max" },
        { key: "illum_b_max.ds_hrly", target: "b_max", base: "illum_b_max" },
        { key: "illum_w_max.ds_hrly", target: "w_max", base: "illum_w_max" },
      ];

      fields.forEach(({ key, target, base }) => {
        const formatted = formatHourly3DData(
          processedData,
          key,
          base,
          timezone
        );
        illuminationData[target] = formatted;
        // console.log(`💡 [Illumination] ${key} data points:`, formatted.length);
        if (formatted.length > 0) {
          console.log(`💡 [Illumination] ${key} sample:`, formatted[0]);
        }
      });

      // console.log("💡 [Illumination] Final data structure:", {
      //   r_mean: illuminationData.r_mean.length,
      //   g_mean: illuminationData.g_mean.length,
      //   b_mean: illuminationData.b_mean.length,
      //   w_mean: illuminationData.w_mean.length,
      //   r_max: illuminationData.r_max.length,
      //   g_max: illuminationData.g_max.length,
      //   b_max: illuminationData.b_max.length,
      //   w_max: illuminationData.w_max.length,
      // });

      return {
        success: true,
        data: illuminationData,
      };
    }

    return {
      success: false,
      error: { message: "Something went wrong" },
    };
  };

  return {
    getPatientDataFromCDL,
    getBedExitMotionData,
    getHeartRateData,
    getPatientDailyRRData,
    getPatientDailyRRWithoutProcessing,
    getPatientHourlyRRWithoutProcessingV2,
    getPatientHourlyHRWithoutProcessingV2,
    getIlluminationData,
  };
};
